import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation as useTranslationRaw } from 'react-i18next';
import * as yaml from 'js-yaml';
import {
  RectangleStackIcon,
  ArrowPathIcon,
  LockClosedIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  InformationCircleIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  ClockIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';

import apiService from '../services/api';
import {
  Project,
  Environment,
  StackPlan,
  StackSummary,
  BrickPlan,
  EnvBinding,
  ConnectedRepository,
} from '../types/api';
import { useOrganisation } from '../contexts/OrganisationContext';
import { showErrorToast, showSuccessToast } from '../utils';
import Logger from '../utils/logger';

// i18next 23 + resources:any fait exploser la profondeur d'instanciation de
// types (TS2589) sur le generic de useTranslation quand un namespace est
// utilise intensivement (clefs dynamiques + defaultValue) comme sur cette page.
// On caste le hook vers une signature simple, dans le meme esprit que le cast
// `i18n as any` de src/i18n.ts. Le typage runtime est inchange.
const useTranslation = useTranslationRaw as unknown as (
  ns?: string
) => { t: (key: string, options?: Record<string, unknown>) => string };

const EXAMPLE_MANIFEST = `name: demo-shop

# --- Bases de données managées (postgres | redis) ---
# La plateforme les provisionne et génère les identifiants automatiquement.
databases:
  - name: db
    type: postgres
    # resources optionnel : sinon des défauts plateforme s'appliquent
    resources: { cpu: 0.5, memory: 1, storage: 2 }   # cpu (cores), memory/storage (Gi)

# --- Services ---
services:
  # 1) Service depuis une IMAGE pré-construite (registry public ou privé)
  - name: api
    type: web                  # web | private | worker | cron
    image: nginxinc/nginx-unprivileged:stable-alpine   # image NON-ROOT (obligatoire : la plateforme lance les pods non-root / rootfs read-only)
    framework: nodejs-express  # requis pour une image CODE (nodejs-express, python-fastapi, react, nextjs, python-django, go-gin, php-laravel...)
    port: 8080                 # nginx-unprivileged écoute sur 8080
    exposePublic: true         # crée un domaine public (web uniquement)
    resources: { cpu: 0.25, memory: 0.5 }
    env:
      # Câblage AUTO — plus besoin de recopier un hostname :
      DATABASE_URL: { fromDatabase: { name: db, property: connectionString } }
      SECRET_KEY: { generateValue: true }   # généré par la plateforme, stable
      LOG_LEVEL: info                        # valeur simple
      # STRIPE_KEY: { sync: false }          # demandé au 1er deploy, jamais écrasé
      # TOKEN: { value: "xxx", secret: true } # secret chiffré

  # 2) Service depuis VOTRE dépôt (buildé par la plateforme) — décommentez :
  # - name: web
  #   type: web
  #   framework: nextjs
  #   build:
  #     repo: https://github.com/vous/votre-repo
  #     branch: main
  #     rootDir: .              # sous-dossier si monorepo
  #   port: 3000
  #   exposePublic: true
  #   dependsOn: [api]
  #   env:
  #     API_URL: { fromService: { name: api, property: url } }   # câblage vers un service voisin
`;

type Step = 'editor' | 'plan' | 'stack';

// ---- Badge color helpers ----------------------------------------------------

const kindBadgeClass = (kind: string): string => {
  const map: Record<string, string> = {
    build: 'bg-blue-500/12 text-blue-400',
    image: 'bg-purple-500/12 text-purple-400',
    managed_db: 'bg-emerald-500/12 text-emerald-400',
  };
  return map[kind] || 'bg-surface-3 text-text-secondary';
};

const stateBadgeClass = (state: string): string => {
  const map: Record<string, string> = {
    live: 'bg-green-500/12 text-green-400',
    built: 'bg-emerald-500/12 text-emerald-400',
    deploying: 'bg-blue-500/12 text-blue-400 animate-pulse',
    building: 'bg-blue-500/12 text-blue-400 animate-pulse',
    build_queued: 'bg-surface-3 text-text-secondary',
    deploy_blocked: 'bg-amber-500/12 text-amber-400',
    declared: 'bg-surface-3 text-text-secondary',
    skipped: 'bg-surface-3 text-text-tertiary',
    failed_build: 'bg-red-500/12 text-red-400',
    failed_deploy: 'bg-red-500/12 text-red-400',
  };
  return map[state] || 'bg-surface-3 text-text-secondary';
};

const syncStatusBadgeClass = (status: string): string => {
  const map: Record<string, string> = {
    synced: 'bg-green-500/12 text-green-400',
    applying: 'bg-blue-500/12 text-blue-400 animate-pulse',
    partial: 'bg-orange-500/12 text-orange-400',
    failed: 'bg-red-500/12 text-red-400',
    planning: 'bg-surface-3 text-text-secondary',
  };
  return map[status] || 'bg-surface-3 text-text-secondary';
};

// A source is "auto-wired" (the selling point) when it references another brick.
const isAutoWiredSource = (source: string): boolean =>
  source.startsWith('database:') || source.startsWith('service:');

const BlueprintPage: React.FC = () => {
  const { t } = (useTranslation as any)('blueprints');
  const { environmentId: environmentIdParam } = useParams<{ environmentId?: string }>();
  const { currentOrganisation } = useOrganisation();

  const [step, setStep] = useState<Step>('editor');
  const [projects, setProjects] = useState<Project[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const [manifestText, setManifestText] = useState<string>('');
  const [planning, setPlanning] = useState(false);
  const [plan, setPlan] = useState<StackPlan | null>(null);

  // ---- "Detect from a repo" panel state -------------------------------------
  const [connectedRepos, setConnectedRepos] = useState<ConnectedRepository[]>([]);
  const [selectedRepoId, setSelectedRepoId] = useState<string>('');
  const [detectRepoUrl, setDetectRepoUrl] = useState<string>('');
  const [detecting, setDetecting] = useState(false);
  const [detectNotes, setDetectNotes] = useState<string[]>([]);

  const [applying, setApplying] = useState(false);
  const [stack, setStack] = useState<StackSummary | null>(null);
  const [reconciling, setReconciling] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dryMode, setDryMode] = useState(false);
  const [existingStacks, setExistingStacks] = useState<StackSummary[]>([]);
  const [loadingStacks, setLoadingStacks] = useState(false);
  const [openingStackId, setOpeningStackId] = useState<string>('');
  // Édition d'un stack existant : id ciblé (Apply → updateStack au lieu d'applyBlueprint).
  const [editingStackId, setEditingStackId] = useState<string>('');

  // ---- Data loading ---------------------------------------------------------

  const loadEnvironments = useCallback(async (projectId: string) => {
    try {
      const envs = await apiService.getProjectEnvironments(projectId);
      setEnvironments(envs);
    } catch (error) {
      Logger.error('Failed to load environments', error);
      setEnvironments([]);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const projs = await apiService.getProjects(currentOrganisation?.id);
        setProjects(projs);

        if (environmentIdParam) {
          try {
            const env = await apiService.getEnvironment(environmentIdParam);
            setSelectedProjectId(env.projet_id);
            const envs = await apiService.getProjectEnvironments(env.projet_id);
            setEnvironments(envs);
            setSelectedEnvironmentId(environmentIdParam);
          } catch (error) {
            Logger.error('Failed to resolve environment from URL', error);
          }
        } else if (projs.length > 0) {
          setSelectedProjectId(projs[0].id);
        }
      } catch (error) {
        showErrorToast(error, t('toast.loadProjectsFailed') as string);
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganisation?.id]);

  // Load connected repositories for the "Detect from a repo" panel
  useEffect(() => {
    const loadRepos = async () => {
      try {
        const repos = await apiService.getConnectedRepositories();
        setConnectedRepos(repos);
      } catch (error) {
        Logger.error('Failed to load connected repositories', error);
        setConnectedRepos([]);
      }
    };
    loadRepos();
  }, []);

  // Load environments when project changes (except when resolved from URL above)
  useEffect(() => {
    if (!selectedProjectId) {
      setEnvironments([]);
      return;
    }
    // Avoid clobbering the env list already resolved from the URL param
    if (environmentIdParam) return;
    loadEnvironments(selectedProjectId);
  }, [selectedProjectId, environmentIdParam, loadEnvironments]);

  // ---- Existing stacks of the selected environment --------------------------

  const loadStacks = useCallback(async (environmentId: string) => {
    if (!environmentId) {
      setExistingStacks([]);
      return;
    }
    try {
      setLoadingStacks(true);
      const list = await apiService.listStacks(environmentId);
      setExistingStacks(list);
    } catch (error) {
      Logger.error('Failed to load stacks', error);
      setExistingStacks([]);
    } finally {
      setLoadingStacks(false);
    }
  }, []);

  useEffect(() => {
    loadStacks(selectedEnvironmentId);
  }, [selectedEnvironmentId, loadStacks]);

  const handleOpenStack = async (stackId: string) => {
    try {
      setOpeningStackId(stackId);
      const fresh = await apiService.getStack(stackId);
      setStack(fresh);
      setStep('stack');
    } catch (error) {
      showErrorToast(error, t('toast.loadFailed') as string);
    } finally {
      setOpeningStackId('');
    }
  };

  // ---- Polling while the stack is applying ----------------------------------

  const stackRef = useRef<StackSummary | null>(null);
  stackRef.current = stack;

  useEffect(() => {
    if (!stack || stack.sync_status !== 'applying') return;
    const interval = setInterval(async () => {
      const current = stackRef.current;
      if (!current) return;
      try {
        setRefreshing(true);
        const fresh = await apiService.getStack(current.id);
        setStack(fresh);
      } catch (error) {
        Logger.error('Failed to poll stack', error);
      } finally {
        setRefreshing(false);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [stack]);

  // ---- Handlers -------------------------------------------------------------

  const parseManifest = (): object | null => {
    try {
      const parsed = yaml.load(manifestText);
      if (!parsed || typeof parsed !== 'object') {
        showErrorToast(null, t('toast.yamlParseError', { message: '—' }) as string);
        return null;
      }
      return parsed as object;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      showErrorToast(null, t('toast.yamlParseError', { message }) as string);
      return null;
    }
  };

  // Extract a readable message from a 422 validation error (detail.errors[]).
  const extractPlanError = (error: unknown): string | null => {
    const apiError = error as { response?: { status?: number; data?: { detail?: unknown } } };
    if (apiError.response?.status !== 422) return null;
    const detail = apiError.response.data?.detail as { errors?: unknown } | undefined;
    if (detail && Array.isArray(detail.errors)) {
      return detail.errors
        .map((e) => (typeof e === 'string' ? e : JSON.stringify(e)))
        .join(' • ');
    }
    return null;
  };

  const handleDetect = async () => {
    const trimmedUrl = detectRepoUrl.trim();
    if (!selectedRepoId && !trimmedUrl) return;

    const payload = selectedRepoId
      ? { connected_repository_id: Number(selectedRepoId) }
      : { repo_url: trimmedUrl };

    try {
      setDetecting(true);
      setDetectNotes([]);
      const res = await apiService.detectBlueprint(payload);
      setManifestText(res.manifest_yaml);
      setDetectNotes(res.notes || []);
      showSuccessToast(t('detect.success') as string);
    } catch (error) {
      // 400 = clone/analyse impossible → remonter le detail renvoyé par l'API
      const apiError = error as { response?: { data?: { detail?: unknown } } };
      const detail = apiError.response?.data?.detail;
      if (typeof detail === 'string' && detail) {
        showErrorToast(null, detail);
      } else {
        showErrorToast(error, t('detect.failed') as string);
      }
    } finally {
      setDetecting(false);
    }
  };

  const handlePlan = async () => {
    if (!selectedEnvironmentId) return;
    const manifestObj = parseManifest();
    if (!manifestObj) return;

    try {
      setPlanning(true);
      const result = await apiService.planBlueprint(selectedEnvironmentId, manifestObj);
      setPlan(result);
      setStep('plan');
    } catch (error) {
      const validationMsg = extractPlanError(error);
      if (validationMsg) {
        showErrorToast(null, validationMsg);
      } else {
        showErrorToast(error, t('toast.planFailed') as string);
      }
    } finally {
      setPlanning(false);
    }
  };

  const handleApply = async () => {
    if (!selectedEnvironmentId) return;
    const manifestObj = parseManifest();
    if (!manifestObj) return;

    try {
      setApplying(true);
      // Édition d'un stack existant → PUT (updateStack) ; sinon création (applyBlueprint).
      const summary = editingStackId
        ? await apiService.updateStack(editingStackId, selectedEnvironmentId, manifestObj)
        : await apiService.applyBlueprint(selectedEnvironmentId, manifestObj, { manifest_source: 'console' });
      setStack(summary);
      setStep('stack');
      setEditingStackId('');
      showSuccessToast(t(editingStackId ? 'toast.updateSuccess' : 'toast.applySuccess') as string);
      loadStacks(selectedEnvironmentId);
    } catch (error) {
      showErrorToast(error, t('toast.applyFailed') as string);
    } finally {
      setApplying(false);
    }
  };

  // Charge le manifeste d'un stack existant dans l'éditeur pour le modifier + ré-appliquer.
  const handleEditManifest = () => {
    if (!stack) return;
    if (!stack.manifest_yaml) {
      showErrorToast(null, t('toast.noManifest') as string);
      return;
    }
    setManifestText(stack.manifest_yaml);
    setEditingStackId(stack.id);
    setPlan(null);
    setStep('editor');
  };

  const handleReconcile = async () => {
    if (!stack) return;
    try {
      setReconciling(true);
      const fresh = await apiService.reconcileStack(stack.id, dryMode);
      setStack(fresh);
      showSuccessToast(t(dryMode ? 'toast.reconcileDrySuccess' : 'toast.reconcileSuccess') as string);
    } catch (error) {
      showErrorToast(error, t('toast.reconcileFailed') as string);
    } finally {
      setReconciling(false);
    }
  };

  const handleRetry = async () => {
    if (!stack) return;
    try {
      setRetrying(true);
      const fresh = await apiService.retryStack(stack.id);
      setStack(fresh);
      showSuccessToast(t('toast.retrySuccess') as string);
    } catch (error) {
      showErrorToast(error, t('toast.retryFailed') as string);
    } finally {
      setRetrying(false);
    }
  };

  const handleDelete = async () => {
    if (!stack) return;
    if (!window.confirm(t('stack.deleteConfirm', { name: stack.name || stack.id }) as string)) return;
    try {
      setDeleting(true);
      await apiService.deleteStack(stack.id);
      showSuccessToast(t('toast.deleteSuccess') as string);
      handleNewStack();
    } catch (error) {
      showErrorToast(error, t('toast.deleteFailed') as string);
    } finally {
      setDeleting(false);
    }
  };

  const handleNewStack = () => {
    setStack(null);
    setPlan(null);
    setEditingStackId('');
    setManifestText('');
    setStep('editor');
    loadStacks(selectedEnvironmentId);   // rafraîchit la liste (post-apply / post-delete)
  };

  // ---- Render helpers -------------------------------------------------------

  const renderKindBadge = (kind: string) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${kindBadgeClass(kind)}`}>
      {t(`kind.${kind}`, { defaultValue: t('kind.unknown') })}
    </span>
  );

  const renderEnvBinding = (binding: EnvBinding) => {
    const wired = isAutoWiredSource(binding.source);
    return (
      <div
        key={binding.key}
        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${
          wired ? 'bg-accent-500/12 border border-accent-500/30' : 'bg-surface-3'
        }`}
      >
        <span className="font-mono text-text-primary">{binding.key}</span>
        <span className="text-text-tertiary">→</span>
        <span className={`font-mono ${wired ? 'text-accent-400 font-medium' : 'text-text-secondary'}`}>
          {binding.source}
        </span>
        {wired && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-accent-500/20 text-accent-400 text-[10px] font-semibold">
            {t('plan.autoWired')}
          </span>
        )}
        {binding.sensitive && (
          <LockClosedIcon
            className="w-3.5 h-3.5 text-orange-400 ml-auto flex-shrink-0"
            title={t('plan.sensitive') as string}
          />
        )}
      </div>
    );
  };

  const renderBrickPlan = (brick: BrickPlan) => (
    <div key={brick.name} className="bg-surface-2 rounded-card border border-border-1 p-4">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="text-base font-semibold text-text-primary">{brick.name}</span>
        {renderKindBadge(brick.kind)}
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-surface-3 text-text-secondary">
          {brick.action}
        </span>
        {brick.service_type && (
          <span className="text-xs text-text-tertiary">{brick.service_type}</span>
        )}
        {brick.exposed ? (
          <span className="inline-flex items-center gap-1 text-xs text-green-400">
            <GlobeAltIcon className="w-3.5 h-3.5" />
            {brick.domain || t('plan.exposedYes')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-text-tertiary">
            <LockClosedIcon className="w-3.5 h-3.5" />
            {t('plan.exposedNo')}
          </span>
        )}
      </div>

      {/* Resources + dependencies */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-text-secondary mb-3">
        {(brick.cpu != null || brick.memory != null || brick.storage != null || brick.replicas != null) && (
          <span>
            <span className="text-text-tertiary">{t('plan.colResources')}: </span>
            {brick.cpu != null && `${brick.cpu} ${t('plan.cpuUnit')}`}
            {brick.memory != null && ` · ${brick.memory} ${t('plan.memoryUnit')}`}
            {brick.storage != null && ` · ${brick.storage} ${t('plan.storageUnit')}`}
            {brick.replicas != null && ` · ×${brick.replicas}`}
          </span>
        )}
        {brick.depends_on.length > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="text-text-tertiary">{t('plan.colDependsOn')}:</span>
            {brick.depends_on.map((dep) => (
              <span key={dep} className="px-1.5 py-0.5 rounded bg-surface-3 text-text-secondary font-mono">
                {dep}
              </span>
            ))}
          </span>
        )}
      </div>

      {/* Env bindings */}
      {brick.env_bindings.length > 0 && (
        <div>
          <p className="text-xs font-medium text-text-tertiary mb-1.5">{t('plan.envBindings')}</p>
          <div className="space-y-1">
            {brick.env_bindings.map(renderEnvBinding)}
          </div>
        </div>
      )}

      {/* Per-brick warnings */}
      {brick.warnings.length > 0 && (
        <ul className="mt-3 space-y-1">
          {brick.warnings.map((w, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-orange-400">
              <ExclamationTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  // ---- Step indicator -------------------------------------------------------

  const stepOrder: Step[] = ['editor', 'plan', 'stack'];
  const currentStepIndex = stepOrder.indexOf(step);

  const renderStepIndicator = () => (
    <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-6 mb-8">
      <div className="flex items-center justify-center mb-4">
        {stepOrder.map((s, idx) => (
          <div key={s} className="flex items-center">
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full ${
                idx <= currentStepIndex ? 'bg-accent-500 text-white' : 'bg-surface-3 text-text-secondary'
              }`}
            >
              {idx < currentStepIndex ? <CheckIcon className="w-5 h-5" /> : idx + 1}
            </div>
            {idx < stepOrder.length - 1 && (
              <div className={`w-24 h-1 mx-2 ${idx < currentStepIndex ? 'bg-accent-500' : 'bg-surface-3'}`}></div>
            )}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-4 text-center text-sm">
        {stepOrder.map((s, idx) => (
          <div key={s}>
            <div
              className={`font-medium ${
                idx === currentStepIndex ? 'text-accent-500' : idx < currentStepIndex ? 'text-text-primary' : 'text-text-secondary'
              }`}
            >
              {t(`steps.${s}`)}
            </div>
            <div className="text-text-tertiary">{t(`steps.${s}Hint`)}</div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <RectangleStackIcon className="w-8 h-8 text-accent-500" />
          <h1 className="text-3xl font-bold text-text-primary">{t('title')}</h1>
        </div>
        <p className="text-text-secondary mt-2 max-w-3xl">{t('subtitle')}</p>
      </div>

      {renderStepIndicator()}

      {/* STEP 1: Editor */}
      {step === 'editor' && (
        <div className="space-y-6">
          {projects.length === 0 ? (
            <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-8 text-center">
              <p className="text-text-secondary mb-4">{t('editor.noProjects')}</p>
              <Link
                to="/projects"
                className="inline-flex items-center px-6 py-3 bg-accent-600 hover:bg-accent-700 text-white font-medium rounded-lg transition-colors"
              >
                {t('editor.createProject')}
              </Link>
            </div>
          ) : (
            <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4">{t('editor.environmentSection')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">{t('editor.project')}</label>
                  <select
                    value={selectedProjectId}
                    onChange={(e) => {
                      setSelectedProjectId(e.target.value);
                      setSelectedEnvironmentId('');
                    }}
                    className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  >
                    <option value="">{t('editor.selectProject')}</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nom}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">{t('editor.environment')}</label>
                  <select
                    value={selectedEnvironmentId}
                    onChange={(e) => setSelectedEnvironmentId(e.target.value)}
                    disabled={!selectedProjectId || environments.length === 0}
                    className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500 disabled:opacity-50"
                  >
                    <option value="">
                      {environments.length === 0 ? t('editor.noEnvironments') : t('editor.selectEnvironment')}
                    </option>
                    {environments.map((env) => (
                      <option key={env.id} value={env.id}>
                        {env.nom}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Existing stacks of this environment (reopen) */}
              {selectedEnvironmentId && existingStacks.length > 0 && (
                <div className="bg-surface-3 border border-border-1 rounded-card p-4 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <RectangleStackIcon className="w-5 h-5 text-accent-500" />
                    <h3 className="text-sm font-semibold text-text-primary">{t('editor.existingStacks')}</h3>
                    {loadingStacks && <ArrowPathIcon className="w-3.5 h-3.5 animate-spin text-text-tertiary" />}
                  </div>
                  <div className="space-y-2">
                    {existingStacks.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => handleOpenStack(s.id)}
                        disabled={openingStackId === s.id}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-surface-2 hover:bg-surface-4 border border-border-1 rounded-lg transition-colors text-left disabled:opacity-50"
                      >
                        <span className="flex items-center gap-3 min-w-0">
                          <span className="font-medium text-text-primary truncate">{s.name || s.id}</span>
                          <span className="text-xs text-text-tertiary shrink-0">
                            {s.bricks.length} {t('editor.bricksCount')}
                          </span>
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${syncStatusBadgeClass(s.sync_status)}`}>
                          {t(`syncStatus.${s.sync_status}`, { defaultValue: s.sync_status })}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Detect from a repo */}
              <div className="bg-surface-3 border border-border-1 rounded-card p-4 mb-6">
                <div className="flex items-center gap-2 mb-1">
                  <MagnifyingGlassIcon className="w-5 h-5 text-accent-500" />
                  <h3 className="text-sm font-semibold text-text-primary">{t('detect.title')}</h3>
                </div>
                <p className="text-xs text-text-tertiary mb-4">{t('detect.subtitle')}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1.5">
                      {t('detect.connectedRepo')}
                    </label>
                    {connectedRepos.length === 0 ? (
                      <Link
                        to="/builds"
                        className="inline-flex items-center text-sm text-accent-500 hover:text-accent-400 transition-colors py-2"
                      >
                        {t('detect.connectFirst')}
                      </Link>
                    ) : (
                      <select
                        value={selectedRepoId}
                        onChange={(e) => {
                          setSelectedRepoId(e.target.value);
                          if (e.target.value) setDetectRepoUrl('');
                        }}
                        className="w-full px-3 py-2 border border-border-1 bg-surface-2 text-text-primary rounded-md focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                      >
                        <option value="">{t('detect.selectRepo')}</option>
                        {connectedRepos.map((repo) => (
                          <option key={repo.id} value={String(repo.id)}>
                            {repo.repo_metadata?.full_name || repo.repo_name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1.5">
                      {t('detect.publicUrl')}
                    </label>
                    <input
                      type="text"
                      value={detectRepoUrl}
                      onChange={(e) => {
                        setDetectRepoUrl(e.target.value);
                        if (e.target.value) setSelectedRepoId('');
                      }}
                      placeholder={t('detect.publicUrlPlaceholder') as string}
                      spellCheck={false}
                      className="w-full px-3 py-2 border border-border-1 bg-surface-2 text-text-primary rounded-md font-mono text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end mt-4">
                  <button
                    type="button"
                    onClick={handleDetect}
                    disabled={detecting || (!selectedRepoId && !detectRepoUrl.trim())}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-accent-600 hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {detecting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      <MagnifyingGlassIcon className="w-4 h-4" />
                    )}
                    {detecting ? t('detect.detecting') : t('detect.button')}
                  </button>
                </div>

                {detectNotes.length > 0 && (
                  <div className="mt-4 bg-blue-500/12 border border-blue-500/30 rounded-md p-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <InformationCircleIcon className="w-4 h-4 text-blue-400" />
                      <p className="text-xs font-semibold text-blue-400">{t('detect.notesTitle')}</p>
                    </div>
                    <ul className="space-y-1">
                      {detectNotes.map((note, i) => (
                        <li key={i} className="text-xs text-blue-300 pl-5">
                          {note}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {editingStackId && (
                <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-md bg-amber-500/12 border border-amber-500/30 text-sm text-amber-300">
                  <PencilSquareIcon className="w-4 h-4 flex-shrink-0" />
                  <span>{t('stack.editingBanner', { name: stack?.name || editingStackId })}</span>
                  <button
                    type="button"
                    onClick={handleNewStack}
                    className="ml-auto text-xs text-amber-300 hover:text-amber-200 underline"
                  >
                    {t('stack.editingCancel')}
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-text-secondary">{t('editor.manifestLabel')}</label>
                <button
                  type="button"
                  onClick={() => setManifestText(EXAMPLE_MANIFEST)}
                  className="text-sm text-accent-500 hover:text-accent-400 transition-colors"
                >
                  {t('editor.loadExample')}
                </button>
              </div>
              <textarea
                value={manifestText}
                onChange={(e) => setManifestText(e.target.value)}
                placeholder={t('editor.manifestPlaceholder') as string}
                spellCheck={false}
                rows={18}
                className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md font-mono text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500 resize-y"
              />

              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={handlePlan}
                  disabled={!selectedEnvironmentId || !manifestText.trim() || planning}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-600 hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                >
                  {planning && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                  {planning ? t('editor.planning') : t('editor.planButton')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* STEP 2: Plan / Review */}
      {step === 'plan' && plan && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-text-primary">
              {plan.name ? `${t('plan.title')} · ${plan.name}` : t('plan.title')}
            </h2>
          </div>

          {/* Totals */}
          <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-5">
            <h3 className="text-sm font-medium text-text-tertiary mb-3">{t('plan.totals')}</h3>
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <div className="text-2xl font-bold text-text-primary">
                  {plan.total_cpu} <span className="text-sm font-normal text-text-tertiary">{t('plan.cpuUnit')}</span>
                </div>
                <div className="text-xs text-text-tertiary">{t('plan.totalCpu')}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-text-primary">
                  {plan.total_memory} <span className="text-sm font-normal text-text-tertiary">{t('plan.memoryUnit')}</span>
                </div>
                <div className="text-xs text-text-tertiary">{t('plan.totalMemory')}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-text-primary">
                  {plan.total_storage} <span className="text-sm font-normal text-text-tertiary">{t('plan.storageUnit')}</span>
                </div>
                <div className="text-xs text-text-tertiary">{t('plan.totalStorage')}</div>
              </div>
              {plan.quota_ok != null && (
                <div className="ml-auto">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                      plan.quota_ok ? 'bg-green-500/12 text-green-400' : 'bg-red-500/12 text-red-400'
                    }`}
                  >
                    {plan.quota_ok ? <CheckCircleIcon className="w-4 h-4" /> : <ExclamationTriangleIcon className="w-4 h-4" />}
                    {plan.quota_ok ? t('plan.quotaOk') : t('plan.quotaExceeded')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Warnings */}
          {plan.warnings.length > 0 && (
            <div className="bg-orange-500/12 border border-orange-500/30 rounded-card p-4">
              <p className="text-sm font-semibold text-orange-400 mb-2">{t('plan.warnings')}</p>
              <ul className="space-y-1">
                {plan.warnings.map((w, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-sm text-orange-300">
                    <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Prompts (secrets to provide) */}
          {plan.prompts.length > 0 && (
            <div className="bg-blue-500/12 border border-blue-500/30 rounded-card p-4">
              <p className="text-sm font-semibold text-blue-400 mb-2">{t('plan.prompts')}</p>
              <ul className="space-y-1">
                {plan.prompts.map((p, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-sm text-blue-300">
                    <KeyIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="font-mono">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Bricks (in plan.order) */}
          {plan.bricks.length === 0 ? (
            <div className="bg-surface-2 rounded-card border border-border-1 p-6 text-center text-text-tertiary">
              {t('plan.noBricks')}
            </div>
          ) : (
            <div className="space-y-3">
              {(plan.order.length > 0
                ? plan.order
                    .map((name) => plan.bricks.find((b) => b.name === name))
                    .filter((b): b is BrickPlan => b !== undefined)
                : plan.bricks
              ).map(renderBrickPlan)}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep('editor')}
              className="px-6 py-2.5 bg-surface-3 hover:bg-surface-4 text-text-secondary font-medium rounded-lg transition-colors"
            >
              {t('plan.back')}
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={applying}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-600 hover:bg-accent-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
            >
              {applying && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
              {applying ? t('plan.applying') : t('plan.apply')}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Stack view */}
      {step === 'stack' && stack && (
        <div className="space-y-6">
          {/* Header card */}
          <div className="bg-surface-2 rounded-card shadow-card border border-border-1 p-5">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-bold text-text-primary">{stack.name || stack.id}</h2>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${syncStatusBadgeClass(
                  stack.sync_status
                )}`}
              >
                {t(`syncStatus.${stack.sync_status}`, { defaultValue: stack.sync_status })}
              </span>
              {refreshing && (
                <span className="inline-flex items-center gap-1 text-xs text-text-tertiary">
                  <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                  {t('stack.refreshing')}
                </span>
              )}
              <span className="ml-auto text-xs text-text-tertiary">
                {t('stack.manifestSource')}: {stack.manifest_source}
              </span>
            </div>
          </div>

          {/* Bricks */}
          {stack.bricks.length === 0 ? (
            <div className="bg-surface-2 rounded-card border border-border-1 p-6 text-center text-text-tertiary">
              {t('stack.noBricks')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stack.bricks.map((brick) => (
                <div key={brick.id} className="bg-surface-2 rounded-card shadow-card border border-border-1 p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-base font-semibold text-text-primary">{brick.name}</span>
                    {renderKindBadge(brick.kind)}
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${stateBadgeClass(
                        brick.state
                      )}`}
                    >
                      {t(`state.${brick.state}`, { defaultValue: brick.state })}
                    </span>
                  </div>
                  {brick.depends_on.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 text-xs text-text-secondary">
                      <span className="text-text-tertiary">{t('stack.dependsOn')}:</span>
                      {brick.depends_on.map((dep) => (
                        <span key={dep} className="px-1.5 py-0.5 rounded bg-surface-3 font-mono">
                          {dep}
                        </span>
                      ))}
                    </div>
                  )}
                  {brick.state === 'deploy_blocked' && (() => {
                    const waiting = brick.depends_on.filter((d) => {
                      const dep = stack.bricks.find((b) => b.name === d);
                      return !dep || dep.state !== 'live';
                    });
                    return (
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-400">
                        <ClockIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>
                          {waiting.length > 0
                            ? t('stack.blockedWaiting', { deps: waiting.join(', ') })
                            : t('stack.blockedGeneric')}
                        </span>
                      </div>
                    );
                  })()}
                  {brick.last_error && (
                    <div className="mt-2 flex items-start gap-1.5 text-xs text-red-400">
                      <ExclamationTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        <span className="font-medium">{t('stack.lastError')}: </span>
                        {brick.last_error}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleNewStack}
                className="px-6 py-2.5 bg-surface-3 hover:bg-surface-4 text-text-secondary font-medium rounded-lg transition-colors"
              >
                {t('stack.newStack')}
              </button>
              <button
                type="button"
                onClick={handleEditManifest}
                title={t('stack.editManifestHint') as string}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-surface-3 hover:bg-surface-4 text-text-primary font-medium rounded-lg transition-colors"
              >
                <PencilSquareIcon className="w-4 h-4" />
                {t('stack.editManifest')}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                title={t('stack.deleteHint') as string}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-50 text-red-500 font-medium rounded-lg transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
                {deleting ? t('stack.deleting') : t('stack.delete')}
              </button>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer" title={t('stack.dryModeHint') as string}>
                <input
                  type="checkbox"
                  checked={dryMode}
                  onChange={(e) => setDryMode(e.target.checked)}
                  className="rounded border-border-1 bg-surface-3 text-accent-500 focus:ring-accent-500"
                />
                {t('stack.dryMode')}
              </label>
              <button
                type="button"
                onClick={handleRetry}
                disabled={retrying || reconciling}
                title={t('stack.retryHint') as string}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-surface-3 hover:bg-surface-4 disabled:opacity-50 text-text-primary font-medium rounded-lg transition-colors"
              >
                <ArrowUturnLeftIcon className={`w-4 h-4 ${retrying ? 'animate-spin' : ''}`} />
                {retrying ? t('stack.retrying') : t('stack.retry')}
              </button>
              <button
                type="button"
                onClick={handleReconcile}
                disabled={reconciling}
                title={t('stack.reconcileHint') as string}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-600 hover:bg-accent-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
              >
                <ArrowPathIcon className={`w-4 h-4 ${reconciling ? 'animate-spin' : ''}`} />
                {reconciling ? t('stack.reconciling') : t('stack.reconcile')}
              </button>
            </div>
          </div>
          {/* Aide : le stack progresse tout seul (sweep serveur + auto-refresh) */}
          <div className="flex items-center gap-1.5 text-xs text-text-tertiary">
            <InformationCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{t('stack.autoHint')}</span>
          </div>
        </div>
      )}
    </>
  );
};

export default BlueprintPage;
